import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  markdownTableAvailableEm,
  toggleMarkdownTaskAt,
  type StickyCanvasObject,
} from "@refinement-canvas/domain";
import { isObjectPlacementTool, resolveObjectCursor, useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useObjectTextEditor } from "../input/use-object-text-editor";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import {
  STICKY_FONT_DEFAULT_PX,
  STICKY_PADDING_PX,
  computeStickyBodyReservePx,
  computeStickySignatureFontPx,
  measureStickyFitFontSize,
} from "../../../lib/sticky-font-fit";
import { resolveStickyTextPalette } from "../../../lib/sticky-text-color";
import {
  resolveCalloutColorVars,
  resolveTableBodyPalette,
  tableBodyPaletteStyleVariables,
} from "../../../lib/text-link-color";
import { MarkdownTextView, type MarkdownTableColumnsChange } from "./markdown-text-view";

interface Props {
  readonly object: StickyCanvasObject;
}

// 付箋オブジェクト。
// - 配置位置に貼り付き、いつでも誰でも文字編集可能
// - サーバへは object.text.update op をキー入力後 50ms debounce で送信する
// - 単一クリックで選択、ダブルクリックで編集モードへ
/**
 * 付箋の既定の背景色（色未指定の付箋を描くときの色）。
 * コネクタからの自動作成の予告ゴーストも同じ色で描くため export する。
 */
export const STICKY_DEFAULT_BACKGROUND = "#fff5b3";

export function StickyObject({ object }: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));
  const readOnly = useCanvasStore((s) => s.readOnly);
  const autoEditObjectId = useCanvasStore((s) => s.autoEditObjectId);
  const setAutoEditObjectId = useCanvasStore((s) => s.setAutoEditObjectId);

  // 付箋本文の文字サイズを、付箋サイズへちょうど収まるよう自動調整した結果（余白を最小化する）。
  const [fittedFontSize, setFittedFontSize] = useState<number>(STICKY_FONT_DEFAULT_PX);

  // 署名（最終更新者名）の文字サイズと、本文が署名と重ならないよう本文下部で確保する高さ。
  // 固定 px だと小さい付箋で本文より署名が大きく見えるため、付箋サイズに連動させる。
  // 本文の予約高さ（bodyReservePx）は自動フィット計算（computeStickyTextArea）と同じ
  // computeStickyBodyReservePx を共有し、フィットされた本文が署名の上へ確実に収まる
  // （overflow クリップ＝文字欠けに頼らず、欠けも重なりも起こさない）ようにする。
  const signatureFontPx = computeStickySignatureFontPx({ boxWidth: object.w, boxHeight: object.h });
  const bodyReservePx = computeStickyBodyReservePx({ boxWidth: object.w, boxHeight: object.h });

  // 付箋の背景色に応じて、本文・署名の文字色を読みやすい色へ解決する（濃い背景では白へ反転）。
  // 文字色はコンテナ div に指定し、本文（編集中の contentEditable / 確定表示の両方）へ inherit させる。
  // 印刷時や PDF 変換時（canvas-pdf-export.ts）でも同じ判定を共有し、見た目どおりの文字色にする。
  const textPalette = resolveStickyTextPalette(object.color);

  // 濃い背景では本文だけでなく自動リンク（LinkifiedText）の文字色も反転させる。リンクは
  // .canvas-linkified-link が var(--color-link)/hover/visited を参照するため、コンテナで
  // これらの CSS 変数を本文色（白）へ上書きすると子孫リンクへ継承され、リンクも読めるようになる。
  // PDF ではリンク文字も本文色で描くため（canvas-pdf-export.ts）、これで画面と PDF のリンク色も一致する。
  // 明るい背景では上書きせず、従来のリンク色（水色/紫）を保つ。
  const linkColorVars: CSSProperties = textPalette.isLightText
    ? ({
        "--color-link": textPalette.body,
        "--color-link-hover": textPalette.body,
        "--color-link-visited": textPalette.body,
        "--color-link-visited-hover": textPalette.body,
      } as CSSProperties)
    : {};
  // 付箋の実際の本文色まで含めて表の奇数・偶数行の不透明な面を解決する。
  const tableBodyPalette = resolveTableBodyPalette({
    bodyColor: textPalette.body,
    fill: object.color,
  });
  const tableBodyColorVars = tableBodyPaletteStyleVariables(tableBodyPalette) as CSSProperties;
  const calloutColorVars = resolveCalloutColorVars(object.color) as CSSProperties;

  // 指定の本文に対して収まる文字サイズを実測し、fittedFontSize を更新する（非編集時の再計算用）。
  const recomputeFit = useCallback(
    (text: string) => {
      const sample = editorRef.current;
      if (!sample) return;
      setFittedFontSize(
        measureStickyFitFontSize({
          text,
          boxWidth: object.w,
          boxHeight: object.h,
          hasSignature: Boolean(object.updatedByName),
          sample,
          // 保存済み列幅を渡さないと、計測だけが自動列幅になり実表示と違う折り返しで
          // 文字サイズが決まる（本文が付箋からはみ出す）。
          ...(object.tableColumnWidths === undefined
            ? {}
            : { tableColumnWidths: object.tableColumnWidths }),
        })
      );
    },
    [object.w, object.h, object.updatedByName, object.tableColumnWidths]
  );

  // 編集中はフォントサイズを再計算せず、直前の確定サイズを固定で使う（入力中に文字サイズが
  // リアルタイムに変わると視線が揺れて入力しづらいため、ライブ調整は行わない）。
  // 署名（最終更新者名）との重なりは、編集中は署名自体を非表示にすることで防ぐ（下の署名描画参照）。
  const editor = useObjectTextEditor({
    objectId: object.id,
    initialText: object.text,
    elementRef: editorRef,
    dispatcher,
    disabled: object.locked,
    // 非編集表示が LinkifiedText のため、リンク付き HTML の貼り付けを [テキスト](URL) で取り込める
    preserveHtmlAnchorsOnPaste: true,
    // 範囲選択して URL を貼り付けたとき、選択部分を [表示テキスト](URL) へ変換する。
    linkifySelectionOnUrlPaste: true,
    // 編集中も Markdown 記法を即時 WYSIWYG 整形する（マーカーは非表示。保存は生テキストのまま）。
    markdown: true,
  });

  // 配置直後（place-sticky で作成した直後）や F2 キーで自動的に編集モードへ入り、すぐ入力できる
  // ようにする。仕組み・rAF 遅延・依存の選び方は TextObject の同名 effect と同じ（詳細コメントは
  // text-object.tsx 参照）。付箋は空のまま確定しても本体が見えるため 0 文字自動削除は行わない。
  const enterEditMode = editor.enterEditMode;
  const handleToggleTask = useCallback(
    (checkMarkOffset: number) => {
      const next = toggleMarkdownTaskAt(object.text, checkMarkOffset);
      if (next === null) return;
      dispatcher.updateText(object.id, next);
    },
    [dispatcher, object.id, object.text]
  );
  useEffect(() => {
    if (autoEditObjectId !== object.id) return;
    if (object.locked) {
      setAutoEditObjectId(null);
      return;
    }
    const raf = requestAnimationFrame(() => {
      enterEditMode();
      setAutoEditObjectId(null);
    });
    return () => cancelAnimationFrame(raf);
  }, [autoEditObjectId, object.id, object.locked, enterEditMode, setAutoEditObjectId]);

  useObjectDrag({
    objectId: object.id,
    elementRef: containerRef,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: editor.isEditing || activeTool === "draw-pen",
  });

  // 非編集時の文字サイズ再計算。
  // - 編集を終了したタイミング（isEditing が false へ遷移）に再計算するのが主目的。
  // - リサイズ確定や他参加者の本文更新で w / h / text が変わったときにも追従する（recomputeFit の依存）。
  // 編集中（isEditing=true）は再計算せず、直前のサイズを保持する（入力中にサイズが暴れないようにする）。
  useLayoutEffect(() => {
    if (editor.isEditing) return;
    recomputeFit(object.text);
  }, [editor.isEditing, object.text, recomputeFit]);

  // 本文中の表の列幅は、参照専用・ロック中・編集中のいずれでもないときだけ調整できる。
  // 配置ツール選択中も対象外にする（既存オブジェクトの上でも新規配置を通す契約を、
  // つまみの 0.5em 幅だけ塞がないため）。
  const canResizeTableColumns =
    !readOnly &&
    object.locked !== true &&
    !editor.isEditing &&
    !isObjectPlacementTool(activeTool);
  const handleResizeTableColumns = useCallback(
    (change: MarkdownTableColumnsChange) => {
      dispatcher.updateTableColumnWidths(
        object.id,
        change.tableIndex,
        change.widthsEm,
        change.tableCount,
        change.headerKey
      );
    },
    [dispatcher, object.id]
  );

  return (
    <div
      ref={containerRef}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="sticky"
      onDoubleClick={(event) => {
        const target = event.target as Element | null;
        if (target?.closest('[data-canvas-task-checkbox="true"]')) return;
        editor.enterEditMode();
      }}
      style={{
        // 濃い背景時のみリンク色（--color-link 系）を本文色へ上書きする（明るい背景では空＝従来色）。
        ...linkColorVars,
        // 表本文の不透明な奇数行面とストライプは、本文色確定後の共通配色を使う。
        ...tableBodyColorVars,
        // callout の配色も背景明暗に応じて切り替える。
        ...calloutColorVars,
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        background: object.color ?? STICKY_DEFAULT_BACKGROUND,
        // 本文の文字色は背景色から解決した読みやすい色（濃い背景では白へ反転）。内側の編集用/表示用
        // 要素へ inherit させ、編集中の入力文字・確定後の表示文字の双方に適用する。
        color: textPalette.body,
        // 選択枠は FigJam 風の水色で、ズーム倍率に依らず画面上一定の太さ（≒2px）にする。
        boxShadow: isSelected
          ? "0 0 0 calc(2px * var(--canvas-inv-zoom, 1)) var(--color-selection), 0 2px 6px rgba(0,0,0,0.15)"
          : "0 2px 6px rgba(0,0,0,0.15)",
        padding: "12px",
        // 文字サイズは付箋サイズと本文から自動算出した値を用いる（編集終了時などに再計算）。
        fontSize: `${fittedFontSize}px`,
        whiteSpace: "pre-wrap",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        // 参照専用では default、編集中は text を優先。非編集時、配置ツール・ペン描画の選択中は新規作成（crosshair）を示し移動と誤認させない。
        cursor: resolveObjectCursor({ readOnly, activeTool, isEditing: editor.isEditing }),
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        userSelect: editor.isEditing ? "text" : "none",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: "28px",
          height: "28px",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.6) 0 50%, rgba(0,0,0,0.12) 51% 100%)",
          clipPath: "polygon(100% 0, 100% 100%, 0 100%)",
          pointerEvents: "none",
        }}
      />
      <div
        // 編集用（contentEditable + 生テキスト）と表示用（Markdown 整形）で DOM ノードの寿命を
        // 分離する（text-object.tsx と同じ理由。key 変更で強制再マウントし、編集中にブラウザが
        // 作った React 管理外ノードと整形ビューの二重表示を構造的に防ぐ）。
        key={editor.isEditing ? "edit" : "view"}
        ref={editorRef}
        data-testid={`canvas-object-text-${object.id}`}
        style={{
          width: "100%",
          outline: "none",
          cursor: "inherit",
          // 文字寄せ（左/中央/右）。未指定は左寄せ。編集中・確定後の両方に適用する。
          // 箇条書き行のみ MarkdownTextView 側で常に左寄せへ上書きする。
          textAlign: object.textAlign ?? "left",
          // 長い英単語などもはみ出さずに折り返す（自動調整の高さ計測と表示の折り返しを一致させる）。
          overflowWrap: "anywhere",
          height: "100%",
          // 最終更新者名と付箋本文がぶつからないよう、本文下部に署名領域（bodyReservePx。自動フィット
          // 計算と同じ値＝署名占有分＋安全余白）を paddingBottom で確保する。フィットされた本文はこの
          // 予約分を除いた高さへ収まるため、overflow クリップに頼らず「本文は全文表示（欠けない）」かつ
          // 「署名と重ならない」を両立する。編集中は署名を非表示にするが、キャレット位置・スクロール挙動を
          // 一定に保つため paddingBottom は同じ値を維持する。
          paddingBottom: object.updatedByName ? `${bodyReservePx}px` : undefined,
          boxSizing: "border-box",
        }}
        {...editor.editorProps}
      >
        {/* Markdown 整形（行単位）の中で URL を LinkifiedText によりリンク化する（両機能を合成）。
            表の使用可能幅は、本文領域の幅（付箋幅 − 内側 padding × 2）と自動調整後の文字サイズから
            求める（DOM の実測に依らない。PDF・サムネイルも同じ式で列幅を決める）。 */}
        {editor.isEditing ? null : (
          <MarkdownTextView
            text={object.text}
            bodyColor={textPalette.body}
            tableBodyPalette={tableBodyPalette}
            availableEm={markdownTableAvailableEm(object.w - STICKY_PADDING_PX * 2, fittedFontSize)}
            baseFontPx={fittedFontSize}
            tableColumnWidths={object.tableColumnWidths}
            rotationDeg={object.rotation}
            onResizeTableColumns={canResizeTableColumns ? handleResizeTableColumns : undefined}
            onToggleTask={
              !readOnly &&
              object.locked !== true &&
              !editor.isEditing &&
              !isObjectPlacementTool(activeTool)
                ? handleToggleTask
                : undefined
            }
          />
        )}
      </div>
      {object.updatedByName && !editor.isEditing ? (
        // 左下に薄く小さく署名表示。文字サイズは付箋サイズに連動した相対値（signatureFontPx）で、
        // 小さい付箋では署名も小さくなる。めくれ三角の左側に収まるよう右側に十分マージンを取る。
        // 編集中は署名を表示しない: 編集中はフォントサイズを固定するため、多く入力すると本文が
        // 署名領域まで達し得るが、署名側を隠すことで重なりを防ぐ（編集確定時に文字サイズが
        // 署名領域を避けて再計算され、署名も再表示される）。
        <div
          aria-label={`本文の最終更新者: ${object.updatedByName}`}
          style={{
            position: "absolute",
            left: "8px",
            bottom: "4px",
            // めくれ三角（右下 28×28）と被らないよう、署名側にも右マージンを設ける。
            right: "32px",
            fontSize: `${signatureFontPx}px`,
            // 署名も背景色に応じて反転（濃い背景では薄い白）し、本文と同様に読めるようにする。
            color: textPalette.signature,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {object.updatedByName}
        </div>
      ) : null}
    </div>
  );
}
